import Base from '@studiometa/js-toolkit/abstracts/Base';
import transition, { setClassesOrStyles } from '@studiometa/js-toolkit/utils/css/transition';
import { isDev } from '../../config';
import ItemSecondLevel from './ItemSecondLevel';

/**
 * ItemFirstLevel class.
 */
export default class ItemFirstLevel extends Base {
  /**
   * Class config.
   * @return {Object}
   */
  get config() {
    return {
      name: 'ItemFirstLevel-Mobile',
      log: isDev(),
      components: {
        ItemSecondLevel,
      },
      styles: {
        submenu: {
          closed: 'transform translate-x-full pointer-events-none',
          active: 'transition duration-700 ease-out-expo',
        },
      },
    };
  }

  /**
   * Get a prefixed ID for the submenu.
   * @return {String}
   */
  get submenuId() {
    return `submenu-${this.$id}`;
  }

  /**
   * Test if the current instance has a submenu.
   * @return {Boolean}
   */
  get hasSubmenu() {
    return !!this.$refs.submenu;
  }

  /**
   * Update position when mouting the component.
   * @return {void}
   */
  mounted() {
    this.submenuIsOpen = false;
    this.isAnimating = false;
    this.unbindMethods = [];

    if (this.hasSubmenu) {
      this.$refs.link.setAttribute('aria-controls', this.submenuId);
      this.$refs.link.setAttribute('aria-expanded', 'false');
      this.$refs.link.setAttribute('aria-haspopup', 'true');
      this.$refs.submenu.setAttribute('id', this.submenuId);
      this.$refs.submenu.classList.add('hidden');
    }

    if (this.$parent) {
      this.unbindMethods = [
        // Always close the submenu when the parent opens
        this.$parent.$on('open', () => this.closeSubmenu(false)),
        this.$parent.$on('close', this.closeSubmenu),
      ];
    }
  }

  /**
   * Close the submenu without animation when destroyed.
   * Delete the `__base__` reference on destroy to allow the desktop version to mount.
   */
  destroyed() {
    setClassesOrStyles(this.$refs.submenu, this.$options.styles.submenu.closed, 'remove');

    this.unbindMethods.forEach((unbindMethod) => unbindMethod());

    // eslint-disable-next-line no-underscore-dangle
    delete this.$el.__base__;
  }

  /**
   * Manage keyboard usage of the submenu.
   *
   * @param  {Boolean} options.isDown If the event is `keydown`.
   * @param  {Boolean} options.ESC    If the key pressed is the escape key.
   * @return {void}
   */
  keyed({ isDown, ESC }) {
    if (isDown && ESC) {
      this.closeSubmenu();
    }
  }

  /**
   * Stop the propagation when clicking inside the menu as it is open to prevent
   * the click binded on the document to be triggered.
   *
   * @param  {MouseEvent} e The click event's object.
   * @return {void}
   */
  onClick(e) {
    if (this.hasSubmenu && this.submenuIsOpen) {
      e.stopPropagation();
    }
  }

  /**
   * Open submenu when clicking on a first level item's link.
   *
   * @param  {MouseEvent} e The click event's object.
   * @return {void}
   */
  onLinkClick(e) {
    if (!this.hasSubmenu) {
      return;
    }

    e.preventDefault();

    if (this.submenuIsOpen) {
      this.closeSubmenu();
    } else {
      this.openSubmenu();
    }
  }

  /**
   * Close btn click handler.
   */
  onCloseClick() {
    this.closeSubmenu();
  }

  /**
   * Open the submenu.
   */
  async openSubmenu() {
    if (!this.hasSubmenu || this.submenuIsOpen || this.isAnimating) {
      return;
    }

    this.$emit('openSubmenu');

    this.submenuIsOpen = true;
    this.isAnimating = true;

    this.$refs.link.setAttribute('aria-expanded', true);
    this.$refs.submenu.setAttribute('aria-hidden', false);
    this.$refs.border.classList.remove('translate-y-full');
    this.$refs.submenu.classList.remove('hidden');
    this.$refs.submenu.scrollTop = 0;

    await transition(this.$refs.submenu, {
      from: this.$options.styles.submenu.closed,
      active: this.$options.styles.submenu.active,
    });
    this.isAnimating = false;
  }

  /**
   * Close the submenu.
   *
   * @param {Boolean}  withAnimation Wether to close the submenu with animation or not.
   */
  async closeSubmenu(withAnimation = true) {
    if (!this.hasSubmenu || !this.submenuIsOpen || (this.isAnimating && withAnimation)) {
      return;
    }

    this.$emit('closeSubmenu');

    this.submenuIsOpen = false;
    this.isAnimating = true;
    this.$refs.link.setAttribute('aria-expanded', 'false');
    this.$refs.submenu.setAttribute('aria-hidden', 'true');
    this.$refs.border.classList.add('translate-y-full');

    await transition(
      this.$refs.submenu,
      {
        active: withAnimation ? this.$options.styles.submenu.active : '',
        to: this.$options.styles.submenu.closed,
      },
      'keep'
    );

    this.$refs.submenu.classList.add('hidden');
    this.isAnimating = false;
  }
}
